import { Component, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { UserPermissionsService } from '@app/core/user-permissions/user-permissions.service';
import { OnboardingService, OnboardingStore } from '@app/modules/onboarding/onboarding.service';
import { CopilotModalComponent } from '@app/shared/components/copilot/copilot-modal/copilot-modal.component';
import { OnDestroyComponent } from '@app/shared/components/on-destroy/on-destroy.component';
import { User } from '@app/shared/interfaces';
import { CopilotService } from '@app/shared/services/copilot/copilot.service';
import { DemoService } from '@app/shared/services/demo/demo.service';
import { IntercomService } from '@app/shared/services/intercom.service';
import { InternalCsService } from '@app/shared/services/internal-cs/internal-cs.service';
import { SidebarCountsService } from '@app/shared/services/sidebar-counts/sidebar-counts.service';
import { Team, UserManagementService } from '@app/shared/services/user-management/user-management.service';
import { environment } from '@env/environment';
import { Observable, combineLatest, take, takeUntil } from 'rxjs';

@Component({
  selector: 'sbnb-menu-mobile',
  templateUrl: './menu-mobile.component.html',
  styleUrls: ['./menu-mobile.component.scss'],
})
export class MenuMobileComponent extends OnDestroyComponent implements OnInit {
  navItems: any[] = [];
  public currentUser$: Observable<User>;
  public expanded: boolean;
  environment = environment;
  debugMode$ = this.csService.debugMode;
  isCS: boolean;
  badgeCounts: object;
  url = window.location.href;
  overrideExpanded: boolean;
  teams: Team[];
  isTeamOwner = true;
  initialized: boolean;
  animEnabled: boolean;

  perms$ = this.userPermissionsService;
  public copilotEnabled$ = this.copilotService.enabled$;

  palantirSrc = '/assets/images/palantir.svg';

  matMenuItems = [
    {
      icon: 'cog-simple',
      link: '/menu-mobile/settings-mobile',
      expandedText: 'Settings',
      expandedTooltipText: 'Manage your subscription and billing information.',
      tooltipText: 'Settings',
      id: 'nav-settings-subscription',
    },
  ];

  matMenuHelperItems = [
    {
      icon: 'hyperlink',
      link: '/referrals',
      expandedText: 'Refer a host',
      expandedTooltipText: 'Refer a host to Hospitable.',
      tooltipText: 'Refer a host',
      id: 'nav-referrals',
    }
  ];

  constructor(
    private authService: AuthenticationService,
    private userPermissionsService: UserPermissionsService,
    private csService: InternalCsService,
    private userManagementService: UserManagementService,
    private sidebarCountsService: SidebarCountsService,
    private onboardingService: OnboardingService,
    public demoService: DemoService,
    private dialog: MatDialog,
    private copilotService: CopilotService,
    private intercomService: IntercomService
  ) {
    super();
  }

  ngOnInit(): void {
    this.currentUser$ = this.authService.user$;

    this.userManagementService.getTeamsForUser().subscribe((res) => {
      this.teams = res;
      this.isTeamOwner = this.teams.some((team) => team.owner === true);
    });

    this.isCS = this.csService.isCS();

    combineLatest([this.currentUser$, this.userPermissionsService.permissions, this.copilotService.enabled$])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([user, permissions, copilotEnabled]) => {
        if (user) {
          if (permissions.is_team_owner || permissions.is_admin || permissions.all_features) {
            this.navItems = this.getNavItems(user, [], true, copilotEnabled);
          } else {
            this.navItems = this.getNavItems(user, permissions.features || [], false, copilotEnabled);
          }

          if (this.navItems.find((item) => item.id === 'nav-calendar')) {
            this.userPermissionsService.calendarPermissions$.pipe(take(1), takeUntil(this.destroyed$)).subscribe();
          }
        }
      });

    this.getBadgeCounts();
  }

  getNavItems(user: User, features: string[] = [], all = false, copilotEnabled = false): any[] {
    const allNavItems = {
      properties: {
        icon: 'properties-simple',
        link: '/properties',
        expandedText: 'Properties',
        expandedTooltipText: 'Manage your listings and properties.',
        tooltipText: 'Properties',
        position: 'top',
        badgeCountKey: 'new_properties',
        id: 'nav-properties',
      },
      gx: {
        icon: 'heart-simple',
        link: '/menu-mobile/gx-mobile',
        expandedText: 'Guest Experience',
        expandedTooltipText: 'Messaging rules for automating \n guest communication.',
        tooltipText: 'Guest Experience',
        position: 'top',
        id: 'nav-gx',
      },
      ops: {
        icon: 'ops-simple',
        link: '/menu-mobile/ops-mobile',
        expandedText: 'Operations',
        expandedTooltipText: 'Task assignment and team communication.',
        tooltipText: 'Operations',
        position: 'top',
        badgeCountKey: 'unassigned_tasks',
        id: 'nav-ops',
      },
      connnectedAccounts: {
        icon: 'key-simple',
        link: '/accounts',
        expandedText: 'Connected Accounts',
        expandedTooltipText: 'Connect your Airbnb, Vrbo & Booking.com accounts to Hospitable.',
        tooltipText: 'Connected Accounts',
        position: 'top',
        id: 'nav-connected-accounts',
      },
      direct: {
        icon: 'direct-simple',
        link: '/menu-mobile/direct-mobile',
        expandedText: 'Direct',
        expandedTooltipText: 'Build your own website tailored to short-term rental bookings.',
        tooltipText: 'Direct',
        position: 'top',
        id: 'nav-direct',
        hidden: false,
      },
      devices: {
        icon: 'devices',
        link: '/menu-mobile/device-mobile',
        expandedText: 'Devices',
        expandedTooltipText: 'Provide guests a seamless experience by using Hospitable to manage your smart devices.',
        tooltipText: 'Devices',
        position: 'top',
        id: 'nav-devices',
        hidden: false,
      },
      copilot: {
        icon: 'magic',
        expandedText: 'Copilot',
        expandedTooltipText: 'Your AI assistant for managing your properties.',
        tooltipText: 'Copilot',
        position: 'bottom',
        id: 'nav-copilot',
        class: 'copilot-nav-item',
        action: () => this.openCopilotModal(),
      },
      apps: {
        icon: 'apps-simple',
        link: '/apps',
        expandedText: 'Apps',
        expandedTooltipText: 'Expanded functionality through \n integrations, webhooks and API. ',
        tooltipText: 'Apps',
        position: 'bottom',
        id: 'nav-apps',
      },
      help: {
        icon: 'help-simple',
        action: () => this.intercomService.openIntercomHelpWithFallback(),
        expandedText: 'Help',
        expandedTooltipText: 'Learn more about Hospitable.',
        tooltipText: 'Support documentation',
        position: 'bottom',
        id: 'nav-help',
        badgeCountKey: 'incomplete_onboarding_steps',
      },
    };

    const nav = [];
    const allFeatures = [
      'properties',
      'gx',
      'ops',
      'connnectedAccounts',
      'direct',
      'devices',
      ...(copilotEnabled ? ['copilot'] : []),
      'apps',
      'help',
      'changelog',
    ];

    if (all) {
      features = allFeatures;
    } else {
      features.push('help', 'changelog');
    }

    allFeatures.forEach((feature) => {
      if (features.includes(feature) && allNavItems[feature]) {
        nav.push(allNavItems[feature]);
      }
    });

    if (features.includes('smartlocks_dashboard') && !all) {
      nav.push(allNavItems['devices']);
    }

    return nav;
  }

  getBadgeCounts(): void {
    this.sidebarCountsService.getCountsFromApi().subscribe((res) => {
      this.badgeCounts = res;

      this.onboardingService.onboardingItems.subscribe((items: OnboardingStore) => {
        // Don't show an unread count if the user has not tried connecting an account yet
        if (!items.meta.has_ever_connected_airbnb_account) {
          return;
        }

        this.badgeCounts['incomplete_onboarding_steps'] = items.meta.pending_count;
      });
    });
  }

  changeActiveTeam(newTeamId: number): void {
    this.userManagementService.switchActiveTeam(newTeamId).subscribe(() => {});
  }

  logout(): void {
    this.authService.logout();
  }

  setDebugMode(): void {
    this.csService.toggleDebugMode();
  }

  enableDemoMode(): void {
    this.demoService.toggleDemoMode(true);
  }

  goToExternalLink(link: string): void {
    if (link) {
      window.location.href = link;
    }
  }

  openCopilotModal(): void {
    this.dialog.open(CopilotModalComponent, {
      width: '100%',
      height: '100%',
      maxWidth: '100vw',
      panelClass: 'copilot-modal',
    });
  }
}
